import React, { useState } from 'react';
import { useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import NotFound from '../../../../assets/html/notFound';
import { BarcodeExportConfigurationContext } from '../../../../context/BarcodeExportConfigurationContext';
import { useToast } from '../../../../providers/ToastProvider';
import { BarcodeExportConfigurationRequest } from '../../../../requests/barcodeExportConfiguration.request';
import { useRequest } from '../../../../requests/common/requestHook';
import {
  BarcodeExportConfigurationInput,
  BarcodeExportConfiguration,
} from '../../../../types/barcode_export_configuration';
import { TranslationKey } from '../../../../types/translation/TranslationKey';
import { translate } from '../../../../types/translation/Translator';
import { Routing } from '../../../../util/routes.util';
import { Breadcrumbs, Crumb } from '../../../components/Breadcrumbs';
import BarcodeSettingsCrumb from '../../../components/Breadcrumbs/crumbs/BarcodeExportConfigurationSettingsCrumb';
import { ToastType } from '../../../components/Toast';
import BarcodeExportConfigurationDeleteModal from './BarcodeExportConfigurationDeleteModal';
import BarcodeExportConfigurationInputPane from './BarcodeExportConfigurationInputPane';

export default function BarcodeExportConfigurationUpdateScreen() {
  const id = useParams()['id'];
  if (!id) return null;

  const navigate = useNavigate();
  const { showToast } = useToast();

  const { barcodeExportConfigurations } = useContext(BarcodeExportConfigurationContext);
  const configuration = barcodeExportConfigurations.get(id);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [update, { loading }] = useRequest(BarcodeExportConfigurationRequest.update);

  const handleSave = async (configuration: BarcodeExportConfigurationInput) => {
    const response = await update({ data: [configuration.forCreate()] });

    if (response) {
      response.forEach((config: BarcodeExportConfiguration) => {
        barcodeExportConfigurations.set(config.id, new BarcodeExportConfiguration(config));
      });
      navigate(Routing.settings.barcodes.overview());
      showToast({
        type: ToastType.success,
        title: translate({ key: TranslationKey.barcodeExportConfigurationUpdatedSuccessfullyToast }),
      });
    }
  };

  if (!configuration) return <NotFound />;

  return (
    <>
      {deleteModalOpen && (
        <BarcodeExportConfigurationDeleteModal
          open={deleteModalOpen}
          setOpen={setDeleteModalOpen}
          configurations={[configuration]}
        />
      )}

      <div className='flex h-full flex-grow flex-col'>
        <Breadcrumbs>
          <BarcodeSettingsCrumb />
          <Crumb
            text={translate({ key: TranslationKey.updateBarcodeExportConfiguration })}
            path={Routing.settings.barcodes.update(id)}
            current
          />
        </Breadcrumbs>

        <div className='content-x content-y-breadcrumbs flex flex-grow flex-col overflow-auto'>
          <BarcodeExportConfigurationInputPane
            configuration={configuration}
            onSave={handleSave}
            loading={loading}
            onDelete={() => setDeleteModalOpen(true)}
          />
        </div>
      </div>
    </>
  );
}
